import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import buildTypeDistribution from '@UI/Pages/Home/DocumentsBlock/TypeDistribution/Helpers/buildTypeDistribution';
import formatCount from '@UI/Pages/Home/Helpers/formatCount';
import homeDocumentTypeSignal, { setHomeDocumentType } from '@UI/Pages/Home/Signals/homeDocumentTypeSignal';
import type DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import type DocumentTypeCountView from '@UI/Store/Indicators/Views/DocumentTypeCountView';
import * as React from 'react';
import VisuallyHiddenText from '@UI/Pages/Home/Shared/VisuallyHiddenText';
import { useSignalValue } from '@verclam/pulsar';
import { Button } from '@verclam/ui-kit';

const groupStyle = css`
	min-width: 0;
	margin: 0;
	border: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 6px;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const SELECTED_BORDER = 'rgba(31, 143, 168, 0.4)';

const rowStyle = (isSelected: boolean) => css`
	display: grid;
	grid-template-columns: minmax(0, 230px) minmax(0, 1fr) 52px;
	align-items: center;
	gap: 1rem;
	width: 100%;
	padding: 0.6875rem 0.75rem;
	text-align: left;
	border: 1.5px solid ${isSelected ? SELECTED_BORDER : 'transparent'};
	border-radius: 12px;
	background-color: ${isSelected ? theme.colors.surface : 'transparent'};
	cursor: pointer;
	transition: background-color ${theme.motion.duration}, border-color ${theme.motion.duration};

	&:hover {
		background-color: ${theme.colors.surface};
	}

	&:focus-visible {
		outline: 3px solid ${theme.colors.accent};
		outline-offset: 2px;
	}

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: minmax(0, 1fr) auto;
		gap: 0.5rem 0.75rem;
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const labelGroupStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5625rem;
	min-width: 0;
`;

const dotStyle = (color: string) => css`
	flex: none;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background-color: ${color};
	transition: background-color ${theme.motion.duration};

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const labelStyle = (isSelected: boolean, isArchived: boolean) => css`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-family: ${theme.fonts.body};
	font-size: 0.84375rem;
	font-weight: ${isSelected ? theme.fontWeights.semibold : theme.fontWeights.medium};
	line-height: 1.35;
	color: ${isArchived ? theme.colors.inkMuted : theme.colors.ink};
`;

const trackStyle = css`
	display: block;
	height: 10px;
	overflow: hidden;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.track};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-column: 1 / -1;
		grid-row: 2;
	}
`;

const fillStyle = (ratio: number, color: string) => css`
	display: block;
	height: 100%;
	width: ${(ratio * 100).toFixed(1)}%;
	border-radius: ${theme.radii.pill};
	background-color: ${color};
	transition: width ${theme.motion.duration}, background-color ${theme.motion.duration};

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const valueStyle = css`
	font-family: ${theme.fonts.mono};
	font-size: 0.875rem;
	font-weight: ${theme.fontWeights.medium};
	line-height: 1;
	text-align: right;
	color: ${theme.colors.ink};
`;

const wrapperStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const footerStyle = css`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
`;

const hintStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	line-height: 1.5;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly byType: readonly DocumentTypeCountView[];
	readonly documentTypes: DocumentTypesListView;
};

/** Horizontal bars in plain CSS; each row is a toggle button that picks (or clears) the type filter. */
const TypeDistribution: React.FC<Props> = ({ byType, documentTypes }) => {
	const selectedTypeId = useSignalValue(homeDocumentTypeSignal);
	const isCompact = useIsCompactViewport();
	const [isExpanded, setIsExpanded] = React.useState(false);
	const baseVisibleCount = isCompact ? 3 : 4;

	const distribution = React.useMemo(
		() =>
			buildTypeDistribution(
				byType,
				documentTypes,
				selectedTypeId,
				isExpanded ? Number.POSITIVE_INFINITY : baseVisibleCount,
			),
		[byType, documentTypes, selectedTypeId, isExpanded, baseVisibleCount],
	);

	const handleToggleExpanded = React.useCallback(() => {
		setIsExpanded((expanded) => !expanded);
	}, []);

	const handleRowClick = React.useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			const documentTypeId = Number(event.currentTarget.dataset.documentTypeId);
			setHomeDocumentType(documentTypeId === selectedTypeId ? null : documentTypeId);
		},
		[selectedTypeId],
	);

	const hasSelection = selectedTypeId !== null;
	const canToggle = distribution.totalTypeCount > baseVisibleCount;
	const scale = distribution.maxValue === 0 ? 1 : distribution.maxValue;

	return (
		<div css={wrapperStyle}>
			<fieldset css={groupStyle}>
				<legend>
					<VisuallyHiddenText>Répartition par type de document</VisuallyHiddenText>
				</legend>
				{distribution.rows.map((row) => {
					const color = hasSelection && !row.isSelected ? row.series.dimmed : row.series.fill;

					return (
						<button
							key={row.documentTypeId}
							type="button"
							css={rowStyle(row.isSelected)}
							aria-pressed={row.isSelected}
							aria-label={`${row.label} : ${formatCount(row.value)} ${row.value > 1 ? 'documents' : 'document'}`}
							data-document-type-id={row.documentTypeId}
							onClick={handleRowClick}
						>
							<span css={labelGroupStyle}>
								<span css={dotStyle(row.series.fill)} aria-hidden="true" />
								<span css={labelStyle(row.isSelected, row.isArchived)}>{row.label}</span>
							</span>
							<span css={trackStyle} aria-hidden="true">
								<span css={fillStyle(row.value / scale, color)} />
							</span>
							<span css={valueStyle} aria-hidden="true">
								{formatCount(row.value)}
							</span>
						</button>
					);
				})}
				{distribution.others !== null && (
					<button
						type="button"
						css={rowStyle(false)}
						aria-label={`Autres types (${distribution.others.typeCount}) : ${formatCount(distribution.others.value)} documents. Afficher tous les types.`}
						onClick={handleToggleExpanded}
					>
						<span css={labelGroupStyle}>
							<span css={dotStyle(theme.home.typeSeriesOther.fill)} aria-hidden="true" />
							<span
								css={labelStyle(false, false)}
							>{`Autres types (${distribution.others.typeCount})`}</span>
						</span>
						<span css={trackStyle} aria-hidden="true">
							<span
								css={fillStyle(
									distribution.others.value / scale,
									hasSelection
										? theme.home.typeSeriesOther.dimmed
										: theme.home.typeSeriesOther.fill,
								)}
							/>
						</span>
						<span css={valueStyle} aria-hidden="true">
							{formatCount(distribution.others.value)}
						</span>
					</button>
				)}
			</fieldset>
			<div css={footerStyle}>
				<p css={hintStyle}>
					{hasSelection
						? 'Type sélectionné : cliquez à nouveau sur la barre pour revenir à tous les types.'
						: 'Cliquez sur une barre pour filtrer par type.'}
				</p>
				{canToggle && (
					<Button
						type="button"
						link
						label={isExpanded ? 'Réduire' : `Voir les ${distribution.totalTypeCount} types`}
						aria-expanded={isExpanded}
						onClick={handleToggleExpanded}
					/>
				)}
			</div>
		</div>
	);
};

export default TypeDistribution;
